import { fetchEventSource } from '@microsoft/fetch-event-source';

/**
 * SSE 事件处理器映射
 * 将特定事件类型映射到处理函数
 */
export interface SSEEventHandlers {
  [eventType: string]: (data: string) => { type: 'message' | 'error'; data?: string; done?: boolean };
}

/**
 * 创建 SSE 事件源的配置选项
 */
export interface CreateSSEEventSourceOptions {
  headers: Record<string, string>;
  eventHandlers: SSEEventHandlers;
  signal?: AbortSignal;
  onError?: (error: Error) => void;
  method?: 'GET' | 'POST';
  body?: Record<string, any> | string;
}

/**
 * 创建 SSE 事件流处理对象
 * 将原始 SSE 事件映射到标准 EventSource 接口
 * 
 * @param url - SSE 端点 URL
 * @param options - 配置选项
 *   - headers: 请求头
 *   - eventHandlers: 事件类型到处理函数的映射
 *   - signal: AbortSignal（可选）
 *   - onError: 错误回调（可选）
 *   - method: HTTP 方法，'GET' 或 'POST'（默认 'GET'）
 *   - body: 请求体，method 为 'POST' 时使用（可选）
 * @returns 类 EventSource 对象
 * 
 * @example
 * ```typescript
 * // GET 请求
 * const eventHandlers: SSEEventHandlers = {
 *   'event.type1': (data) => ({ type: 'message', data }),
 *   'event.done': () => ({ type: 'message', done: true }),
 * };
 * 
 * const eventSource = await createSSEEventSource(url, { headers, eventHandlers, signal });
 * eventSource.onmessage = (e) => console.log(e.data);
 * 
 * // POST 请求
 * const postEventSource = await createSSEEventSource(url, {
 *   headers,
 *   eventHandlers,
 *   method: 'POST',
 *   body: { query: 'test' },
 *   signal
 * });
 * ```
 */
export const createSSEEventSource = async (
  url: string,
  options: CreateSSEEventSourceOptions,
): Promise<EventSource> => {
  const { headers, eventHandlers, signal, onError, method = 'GET', body } = options;

  let closed = false;
  const fakeEventSource = {
    onmessage: null as ((this: EventSource, ev: MessageEvent) => unknown) | null,
    onerror: null as ((this: EventSource, ev: Event) => unknown) | null,
    close: function () {
      closed = true;
    },
    dispatchEvent: (event: Event) => {
      if (event.type === 'message' && fakeEventSource.onmessage) {
        fakeEventSource.onmessage.call(fakeEventSource as unknown as EventSource, event as MessageEvent);
      } else if (event.type === 'error' && fakeEventSource.onerror) {
        fakeEventSource.onerror.call(fakeEventSource as unknown as EventSource, event);
      }
      return true;
    },
  } as EventSource;

  // 处理请求体
  let requestBody: string | undefined;
  if (body) {
    requestBody = typeof body === 'string' ? body : JSON.stringify(body);
  }

  // 使用 fetchEventSource 进行 SSE 连接
  (async () => {
    try {
      await fetchEventSource(url, {
        method,
        headers: {
          ...headers,
          'Accept': 'text/event-stream',
        },
        body: requestBody,
        signal,
        onmessage(event) {
          if (closed) return;
          try {
            const handler = eventHandlers[event.event];
            if (handler) {
              const result = handler(event.data);
              const messageData = result.data
                ? result.data
                : JSON.stringify({ done: result.done });

              fakeEventSource.dispatchEvent(
                new MessageEvent(result.type, { data: messageData })
              );

              if (result.done) {
                closed = true;
              }
            }
          } catch (e) {
            console.error('Error parsing SSE data:', e);
          }
        },
        onerror(error) {
          if (signal?.aborted || closed) {
            return;
          }
          console.error('SSE error:', error);
          onError?.(error);
          fakeEventSource.dispatchEvent(new Event('error'));
          // 通过 throw 停止重试
          throw error;
        },
        openWhenHidden: true,
      });
    } catch (error) {
      if (signal?.aborted || closed) {
        return;
      }
      console.error('SSE fetch error:', error);
      onError?.(error as Error);
      fakeEventSource.dispatchEvent(new Event('error'));
    }
  })();

  if (signal) {
    signal.addEventListener('abort', () => {
      fakeEventSource.close();
    }, { once: true });
  }

  return fakeEventSource;
};
